'use client';

import { c as _c } from "react/compiler-runtime";
import './search-results.scss';
import { useTranslation } from 'react-i18next';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Text } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { trackSelectContent } from '@sola/tracking/components/datalayer.tsx';
import { CardList } from '../../molecules/card-list/card-list.tsx';
import { Card } from '../../molecules/card/card.tsx';
import { Pagination } from '../../molecules/pagination/pagination.tsx';
import { useSearchQueryParams } from '../../utils/query-params.tsx';
import { useSearchAPI } from '../../utils/search.tsx';
import { getHighlightedText } from '../../utils/text-utils.tsx';
import { SearchResultsSkeleton } from './search-results-skeleton.tsx';
type SearchResultsListProps = {
  queryText: string;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
};
const SearchResultsText = t0 => {
  const $ = _c(8);
  const {
    queryText,
    total,
    resultsPerPage,
    currentPage
  } = t0;
  const resultsRemainder = currentPage * resultsPerPage + resultsPerPage;
  const noSearch = queryText.length === 0;
  const hasResults = !!total && total > 0 && queryText.length > 0;
  let t1;
  if ($[0] !== currentPage || $[1] !== hasResults || $[2] !== noSearch || $[3] !== queryText || $[4] !== resultsPerPage || $[5] !== resultsRemainder || $[6] !== total) {
    t1 = <Text><p>{hasResults || noSearch ? <IntlBlock i18nKey={`articles:${queryText.length === 0 ? "resultsWithPagination" : "resultsWithPaginationQuery"}`} values={{
          start: currentPage * resultsPerPage + 1,
          end: resultsRemainder > total ? total : resultsRemainder,
          total,
          query: queryText
        }} components={{
          1: <strong />
        }} shouldUnescape={true} /> : <IntlBlock i18nKey="articles:noResults" values={{
          query: queryText
        }} components={{
          1: <strong />
        }} shouldUnescape={true} />}</p></Text>;
    $[0] = currentPage;
    $[1] = hasResults;
    $[2] = noSearch;
    $[3] = queryText;
    $[4] = resultsPerPage;
    $[5] = resultsRemainder;
    $[6] = total;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
export const SearchResultsList = t0 => {
  const $ = _c(24);
  const {
    queryText,
    itemsPerPage,
    currentPage,
    onPageChange
  } = t0;
  const {
    searchFilters
  } = useSearchQueryParams();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["skippy"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const t2 = searchFilters || "";
  const t3 = currentPage || 0;
  let t4;
  if ($[1] !== t2 || $[2] !== t3) {
    t4 = {
      q: t2,
      page: t3
    };
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    list,
    queryError,
    isLoading
  } = useSearchAPI(queryText, itemsPerPage, t4);
  const hasResults = !!list && !!list?.totalCount && list.totalCount > 0;
  const hasSearchTerm = queryText.trim().length > 0;
  if (queryError && !isLoading) {
    let t5;
    if ($[4] !== t) {
      t5 = t("skippy:siteSearch.error");
      $[4] = t;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== t5) {
      t6 = <Alert variant="negative" message={t5} />;
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  if (isLoading && hasSearchTerm) {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SearchResultsSkeleton />;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  let t5;
  if ($[9] !== currentPage || $[10] !== hasResults || $[11] !== isLoading || $[12] !== itemsPerPage || $[13] !== list || $[14] !== onPageChange || $[15] !== queryText) {
    t5 = hasResults && queryText.length > 0 && !isLoading && <><SearchResultsText queryText={queryText} total={list?.totalCount} resultsPerPage={itemsPerPage} currentPage={currentPage} /><CardList columns={1} columnsMd={1} columnsLg={1}>{list.items.map(item => <Card key={item.contentfulEntryID} title={item.pageTitle} body={<p>{getHighlightedText(item.summary, queryText)}</p>} variant="listItem" primaryCTA={item.path ? {
          title: "Read more",
          link: item.path
        } : undefined} onClick={() => trackSelectContent(item.contentfulEntryID, item.pageTitle, item.contentfulType, [...(item.taxonomy?.articleTopics ?? []), ...(item.taxonomy?.articleThemes ?? [])])} />)}</CardList><Pagination itemsPerPage={itemsPerPage} totalItems={list.totalCount || 0} onPageChange={onPageChange} initialPage={currentPage} /></>;
    $[9] = currentPage;
    $[10] = hasResults;
    $[11] = isLoading;
    $[12] = itemsPerPage;
    $[13] = list;
    $[14] = onPageChange;
    $[15] = queryText;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== hasResults || $[18] !== isLoading || $[19] !== queryText) {
    t6 = !hasResults && queryText.length > 0 && !isLoading && <Text><p><IntlBlock i18nKey="articles:noResults" values={{
          query: queryText
        }} components={{
          1: <strong />
        }} shouldUnescape={true} /></p></Text>;
    $[17] = hasResults;
    $[18] = isLoading;
    $[19] = queryText;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t5 || $[22] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
};